import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography } from '@mui/material';
import { DashboardProvider } from '../../contexts/DashboardContext';
import { AdminPreviewDataProvider } from '../../contexts/AdminPreviewDataContext';
import Calendar from '../common/Calendar';
import TimeHeatmap from '../common/TimeHeatmap';
import DateTimeHeatmap from '../common/DateTimeHeatmap';
import EventEffect from '../common/EventEffect';
import YearlyTrendGrid from '../trend/YearlyTrendGrid';
import MonthlyTrendGrid from '../trend/MonthlyTrendGrid';
import WeeklyTrendGrid from '../trend/WeeklyTrendGrid';
import { getPreviewConfig } from '../../config/adminThresholdPreview';

function AdminProductionPreview({
  actionGroup,
  location,
  graphAction,
  dashboardData,
  year,
  month,
  eventDay,
  title,
  isMobile = false,
}) {
  const config = getPreviewConfig(actionGroup);
  const monthStr = String(month).padStart(2, '0');
  const yearStr = String(year);
  const action = graphAction || config.graphAction;

  const emptyState = useMemo(
    () => ({
      data: null,
      calendarData: [],
      aiAdvice: null,
      weatherData: [],
      eventData: [],
      previousMonthCalendarData: [],
      previousMonthWeatherData: [],
      previousMonthEventData: [],
      previousYear: null,
      previousMonth: null,
      predictionMeta: null,
      responseType: null,
      loading: false,
      error: null,
    }),
    []
  );

  const dataValue = dashboardData || emptyState;

  if (!location || !action) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }

  const renderGraph = () => {
    if (dataValue.loading) {
      return <CircularProgress size={22} sx={{ my: 2 }} />;
    }
    if (dataValue.error) {
      return (
        <Typography variant="caption" color="error">
          {dataValue.error}
        </Typography>
      );
    }

    if (config.kind === 'trend') {
      if (action === 'year_trend') {
        return <YearlyTrendGrid data={dataValue.data || []} loading={false} isMobile={isMobile} />;
      }
      if (action === 'month_trend') {
        return <MonthlyTrendGrid data={dataValue.data || []} loading={false} isMobile={isMobile} />;
      }
      if (action === 'week_trend') {
        return <WeeklyTrendGrid data={dataValue.data || []} loading={false} isMobile={isMobile} />;
      }
    }

    if (config.kind === 'event_effect') {
      return (
        <EventEffect
          initialDay={eventDay || ''}
          lockDayPicker={Boolean(eventDay)}
          prefetchedData={dataValue.data}
          prefetchedEvents={dataValue.eventData}
        />
      );
    }

    if (config.kind === 'graph') {
      if (action.startsWith('cal_')) return <Calendar />;
      if (action.startsWith('wti_')) return <TimeHeatmap />;
      if (action.startsWith('dti_')) return <DateTimeHeatmap />;
    }

    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        '& .MuiContainer-root': { maxWidth: '100% !important', px: { xs: 0.5, sm: 1 } },
        '& section': { py: 0.5 },
      }}
    >
      {title && (
        <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5, fontSize: 11 }}>
          {title}
        </Typography>
      )}
      <DashboardProvider
        initialLocation={location}
        initialAction={action}
        initialYear={yearStr}
        initialMonth={monthStr}
      >
        <AdminPreviewDataProvider value={dataValue}>{renderGraph()}</AdminPreviewDataProvider>
      </DashboardProvider>
    </Box>
  );
}

AdminProductionPreview.propTypes = {
  actionGroup: PropTypes.string.isRequired,
  location: PropTypes.string,
  graphAction: PropTypes.string,
  dashboardData: PropTypes.object,
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  eventDay: PropTypes.string,
  title: PropTypes.string,
  isMobile: PropTypes.bool,
};

export default AdminProductionPreview;
